import React, { useEffect, useMemo, useState } from "react";
import {
  Avatar,
  Button,
  Card,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Grid,
  IconButton,
  Typography,
  CircularProgress,
  Box,
  Stack,
  Modal,
  alpha,
} from "@mui/material";
import { motion } from "framer-motion";
import HomeLayout from "../../asset/layout/HomeLayout";
import SearchBar from "../../components/mui/SearchBar";
import { useDispatch } from "react-redux";
import { enqueueSnackbar } from "notistack";
import { getAllSales, updateSales } from "../../actions/sales.Auth";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import { useNavigate } from "react-router-dom";
import ResponsiveDatePickers from "../../components/mui/PichDate";
import { saveAs } from "file-saver";
import * as XLSX from "xlsx";
import CustomButton from "../../components/mui/CustomButton";
import dayjs from "dayjs";
import PointOfSaleOutlinedIcon from "@mui/icons-material/PointOfSaleOutlined";
import PendingActionsOutlinedIcon from "@mui/icons-material/PendingActionsOutlined";
import CheckCircleOutlineOutlinedIcon from "@mui/icons-material/CheckCircleOutlineOutlined";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import FilterListOutlinedIcon from "@mui/icons-material/FilterListOutlined";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import CloseIcon from "@mui/icons-material/Close";

const columns = [
  "Category",
  "Brand group",
  "Product",
  "Qty",
  "Dealer code",
  "Dealer name",
  "Architect",
  "Customer",
  "Updated",
  "Remarks",
  "Reward",
  "Status",
];

const fadeUp = {
  hidden: { opacity: 0, y: 16 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.04, duration: 0.45, ease: [0.22, 1, 0.36, 1] },
  }),
};

const PanelCard = ({ children, sx = {} }) => (
  <Card
    elevation={0}
    sx={{
      borderRadius: "24px",
      border: "1px solid",
      borderColor: alpha("#11409B", 0.08),
      bgcolor: "#fff",
      boxShadow: "0 4px 32px rgba(15, 23, 42, 0.06)",
      overflow: "hidden",
      ...sx,
    }}
  >
    {children}
  </Card>
);

const StatCard = ({ icon: Icon, label, value, accent, gradient }) => (
  <PanelCard sx={{ height: "100%" }}>
    <Box sx={{ p: 2.5 }}>
      <Stack direction="row" alignItems="center" spacing={2}>
        <Box
          sx={{
            width: 48,
            height: 48,
            borderRadius: "14px",
            background: gradient,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            boxShadow: `0 8px 24px ${alpha(accent, 0.35)}`,
          }}
        >
          <Icon sx={{ color: "#fff", fontSize: 24 }} />
        </Box>
        <Box>
          <Typography
            variant="caption"
            sx={{
              color: "#64748b",
              fontWeight: 600,
              textTransform: "uppercase",
              letterSpacing: "0.06em",
              fontSize: "0.7rem",
            }}
          >
            {label}
          </Typography>
          <Typography
            sx={{
              fontWeight: 800,
              fontSize: "1.75rem",
              color: "#0f172a",
              letterSpacing: "-0.03em",
              lineHeight: 1.1,
            }}
          >
            {value ?? "—"}
          </Typography>
        </Box>
      </Stack>
    </Box>
  </PanelCard>
);

const getStatusStyles = (status) => {
  const s = String(status || "").toUpperCase();
  if (s === "APPROVED") return { color: "#16a34a", bg: alpha("#16a34a", 0.12) };
  if (s === "DECLINED") return { color: "#dc2626", bg: alpha("#dc2626", 0.12) };
  if (s === "PENDING") return { color: "#d97706", bg: alpha("#d97706", 0.12) };
  return { color: "#64748b", bg: alpha("#64748b", 0.12) };
};

const StatusChip = ({ status }) => {
  const styles = getStatusStyles(status);
  return (
    <Chip
      label={status || "—"}
      size="small"
      sx={{
        fontWeight: 700,
        fontSize: "0.7rem",
        color: styles.color,
        bgcolor: styles.bg,
        border: `1px solid ${alpha(styles.color, 0.2)}`,
      }}
    />
  );
};

const display = (val) => (val != null && val !== "" ? val : "—");

export const SalesManagement = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedDateFrom, setSelectedDateFrom] = useState(null);
  const [selectedDateTo, setSelectedDateTo] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [countPerPage, setCountPerPage] = useState(10);
  const [allSales, setAllSales] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedSales, setSelectedSales] = useState(null);

  useEffect(() => {
    if (!localStorage.getItem("token")) {
      navigate("/");
    }
  }, [navigate]);

  useEffect(() => {
    fetchSales();
  }, [searchTerm, selectedDateFrom, selectedDateTo]);

  const applyFilters = (salesData) => {
    let filteredSales = salesData || [];

    if (searchTerm) {
      filteredSales = filteredSales.filter((item) =>
        [
          item.category,
          item.brandGroup,
          item.product,
          item.quantity?.toString(),
          item.dealerCode,
          item.dealerName,
          item.architechId?.phone,
        ].some((field) =>
          field?.toLowerCase().includes(searchTerm.toLowerCase())
        )
      );
    }

    if (selectedDateFrom || selectedDateTo) {
      filteredSales = filteredSales.filter((item) => {
        const createdAt = new Date(item.createdAt);
        let isAfterStartDate = true;
        if (selectedDateFrom) {
          isAfterStartDate = createdAt >= selectedDateFrom;
        }
        let isBeforeEndDate = true;
        if (selectedDateTo) {
          const endOfDay = new Date(selectedDateTo);
          endOfDay.setHours(23, 59, 59, 999);
          isBeforeEndDate = createdAt <= endOfDay;
        }
        return isAfterStartDate && isBeforeEndDate;
      });
    }

    return filteredSales;
  };

  const fetchSales = () => {
    setLoading(true);
    dispatch(
      getAllSales(
        { page: 1, perPage: 100000 },
        (data) => {
          const filteredSales = applyFilters(data.data.data.hamrahis);
          setAllSales(filteredSales);
          setLoading(false);
        },
        () => setLoading(false)
      )
    );
  };

  const stats = useMemo(() => {
    const total = allSales.length;
    const pending = allSales.filter((s) => s.status === "PENDING").length;
    const approved = allSales.filter((s) => s.status === "APPROVED").length;
    const declined = allSales.filter((s) => s.status === "DECLINED").length;
    return { total, pending, approved, declined };
  }, [allSales]);

  const handleSearch = (term) => {
    setSearchTerm(term);
    setCurrentPage(1);
  };

  const handleStatusUpdate = (id, newStatus) => {
    dispatch(
      updateSales(
        id,
        { status: newStatus },
        () => {
          enqueueSnackbar("Sales Updated Successfully", { variant: "success" });
          fetchSales();
        },
        (error) => {
          enqueueSnackbar(error?.response?.data?.message, { variant: "error" });
        }
      )
    );
  };

  const exportToExcel = () => {
    const header = [
      "Category",
      "Brand Group",
      "Product",
      "Quantity",
      "Dealer Code",
      "Dealer Name",
      "Architech",
      "Customer Details",
      "Updated On",
      "Remarks",
      "Reward Point",
      "Status",
    ];

    const rows = allSales.map((sale) => [
      sale?.category,
      sale?.brandGroup,
      sale?.product,
      sale?.quantity,
      sale?.dealerCode,
      sale?.dealerName,
      sale?.architechId?.phone,
      sale?.customerDetails,
      sale?.updatedAt,
      sale?.remarks,
      sale?.reward,
      sale?.status,
    ]);

    rows.unshift(header);
    const worksheet = XLSX.utils.aoa_to_sheet(rows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Sales Data");
    const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
    saveAs(
      new Blob([excelBuffer], { type: "application/octet-stream" }),
      "Sales_Data.xlsx"
    );
  };

  const totalPages = Math.ceil(allSales.length / countPerPage) || 1;
  const currentSales = allSales.slice(
    (currentPage - 1) * countPerPage,
    currentPage * countPerPage
  );

  const handleSearchClear = () => {
    setSearchTerm("");
    setSelectedDateFrom(null);
    setSelectedDateTo(null);
    setCurrentPage(1);
  };

  const handlePhoneClick = (sale) => {
    setSelectedSales(sale);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedSales(null);
  };

  const premiumButtonSx = {
    height: "44px",
    borderRadius: "12px",
    fontWeight: 600,
    textTransform: "none",
    background: "linear-gradient(135deg, #11409B 0%, #2563eb 100%)",
    boxShadow: "0 6px 24px rgba(37, 99, 235, 0.35)",
    "&:hover": {
      background: "linear-gradient(135deg, #0f3580 0%, #1d4ed8 100%)",
    },
  };

  return (
    <HomeLayout>
      <Box
        sx={{
          minHeight: "100%",
          background:
            "linear-gradient(180deg, #f0f4fa 0%, #f5f7fa 35%, #eef2f7 100%)",
          mx: -3,
          mt: -3,
          px: 3,
          pt: 3,
          pb: 4,
        }}
      >
        <motion.div initial="hidden" animate="visible" variants={fadeUp}>
          <Box
            sx={{
              p: { xs: 3, md: 4 },
              mb: 3,
              borderRadius: "24px",
              background:
                "linear-gradient(135deg, #0c3483 0%, #11409B 40%, #1e40af 70%, #2563eb 100%)",
              color: "#fff",
              position: "relative",
              overflow: "hidden",
              boxShadow: "0 20px 60px rgba(17, 64, 155, 0.35)",
            }}
          >
            <Box
              sx={{
                position: "absolute",
                top: -50,
                right: -30,
                width: 200,
                height: 200,
                borderRadius: "50%",
                bgcolor: alpha("#fff", 0.06),
              }}
            />
            <Stack
              direction={{ xs: "column", sm: "row" }}
              alignItems={{ xs: "flex-start", sm: "center" }}
              justifyContent="space-between"
              spacing={2}
              sx={{ position: "relative", zIndex: 1 }}
            >
              <Box>
                <Stack direction="row" alignItems="center" spacing={1} mb={1}>
                  <PointOfSaleOutlinedIcon />
                  <Typography
                    variant="caption"
                    sx={{ fontWeight: 600, letterSpacing: "0.1em", opacity: 0.85 }}
                  >
                    SALES
                  </Typography>
                </Stack>
                <Typography
                  variant="h4"
                  sx={{ fontWeight: 800, letterSpacing: "-0.03em" }}
                >
                  Sales management
                </Typography>
                <Typography sx={{ mt: 1, opacity: 0.82, maxWidth: 520 }}>
                  Review hamrahi sales entries, approve or decline pending
                  records, and export filtered data.
                </Typography>
              </Box>
              <CustomButton
                variant="contained"
                onClick={exportToExcel}
                startIcon={<FileDownloadOutlinedIcon />}
                sx={{
                  ...premiumButtonSx,
                  bgcolor: alpha("#fff", 0.15),
                  backdropFilter: "blur(8px)",
                  border: `1px solid ${alpha("#fff", 0.25)}`,
                  boxShadow: "none",
                  "&:hover": { bgcolor: alpha("#fff", 0.22) },
                }}
              >
                Export sales
              </CustomButton>
            </Stack>
          </Box>
        </motion.div>

        <Grid container spacing={2.5} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <motion.div custom={0} initial="hidden" animate="visible" variants={fadeUp}>
              <StatCard
                icon={PointOfSaleOutlinedIcon}
                label="Total records"
                value={stats.total}
                accent="#4f46e5"
                gradient="linear-gradient(135deg, #818cf8 0%, #4f46e5 100%)"
              />
            </motion.div>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <motion.div custom={1} initial="hidden" animate="visible" variants={fadeUp}>
              <StatCard
                icon={PendingActionsOutlinedIcon}
                label="Pending"
                value={stats.pending}
                accent="#d97706"
                gradient="linear-gradient(135deg, #fbbf24 0%, #d97706 100%)"
              />
            </motion.div>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <motion.div custom={2} initial="hidden" animate="visible" variants={fadeUp}>
              <StatCard
                icon={CheckCircleOutlineOutlinedIcon}
                label="Approved"
                value={stats.approved}
                accent="#059669"
                gradient="linear-gradient(135deg, #34d399 0%, #059669 100%)"
              />
            </motion.div>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <motion.div custom={3} initial="hidden" animate="visible" variants={fadeUp}>
              <StatCard
                icon={CancelOutlinedIcon}
                label="Declined"
                value={stats.declined}
                accent="#dc2626"
                gradient="linear-gradient(135deg, #f87171 0%, #dc2626 100%)"
              />
            </motion.div>
          </Grid>
        </Grid>

        <motion.div custom={4} initial="hidden" animate="visible" variants={fadeUp}>
          <PanelCard sx={{ mb: 3, p: { xs: 2, md: 2.5 } }}>
            <Stack direction="row" alignItems="center" spacing={1} mb={2}>
              <FilterListOutlinedIcon sx={{ color: "#11409B", fontSize: 20 }} />
              <Typography sx={{ fontWeight: 700, color: "#0f172a" }}>
                Filters
              </Typography>
            </Stack>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={4}>
                <SearchBar
                  onSearch={handleSearch}
                  searchTerm={searchTerm}
                  height="2.75rem"
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <ResponsiveDatePickers
                  selectedDate={selectedDateFrom}
                  setSelectedDate={setSelectedDateFrom}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <ResponsiveDatePickers
                  selectedDate={selectedDateTo}
                  setSelectedDate={setSelectedDateTo}
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <CustomButton
                  variant="outlined"
                  onClick={handleSearchClear}
                  fullWidth
                  sx={{
                    height: "2.75rem",
                    borderRadius: "12px",
                    textTransform: "none",
                    fontWeight: 600,
                    borderColor: alpha("#11409B", 0.25),
                    color: "#11409B",
                    "&:hover": {
                      borderColor: "#11409B",
                      bgcolor: alpha("#11409B", 0.04),
                    },
                  }}
                >
                  Clear
                </CustomButton>
              </Grid>
            </Grid>
          </PanelCard>
        </motion.div>

        <motion.div custom={5} initial="hidden" animate="visible" variants={fadeUp}>
          {loading ? (
            <PanelCard>
              <Stack alignItems="center" justifyContent="center" sx={{ py: 10 }}>
                <CircularProgress sx={{ color: "#11409B" }} />
                <Typography sx={{ mt: 2, color: "#64748b" }}>
                  Loading sales…
                </Typography>
              </Stack>
            </PanelCard>
          ) : currentSales.length > 0 ? (
            <TableContainer component={PanelCard} sx={{ overflowX: "auto" }}>
              <Table>
                <TableHead>
                  <TableRow
                    sx={{
                      bgcolor: alpha("#11409B", 0.04),
                      "& th": {
                        borderBottom: `1px solid ${alpha("#11409B", 0.08)}`,
                      },
                    }}
                  >
                    {columns.map((column) => (
                      <TableCell
                        key={column}
                        sx={{
                          fontSize: "0.75rem",
                          fontWeight: 700,
                          color: "#11409B",
                          textTransform: "uppercase",
                          letterSpacing: "0.06em",
                          py: 2,
                          whiteSpace: "nowrap",
                        }}
                      >
                        {column}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {currentSales.map((row) => (
                    <TableRow
                      key={row._id || row.id}
                      sx={{
                        transition: "background 0.2s",
                        "&:hover": { bgcolor: alpha("#11409B", 0.03) },
                        "& td": {
                          borderBottom: `1px solid ${alpha("#11409B", 0.06)}`,
                          py: 1.75,
                          whiteSpace: "nowrap",
                        },
                      }}
                    >
                      <TableCell>
                        <Chip
                          label={display(row?.category)}
                          size="small"
                          sx={{
                            bgcolor: alpha("#11409B", 0.08),
                            color: "#11409B",
                            fontWeight: 600,
                            fontSize: "0.7rem",
                          }}
                        />
                      </TableCell>
                      <TableCell>{display(row?.brandGroup)}</TableCell>
                      <TableCell>
                        <Typography sx={{ fontWeight: 600, color: "#0f172a" }}>
                          {display(row?.product)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography sx={{ fontWeight: 700, color: "#0d9488" }}>
                          {display(row?.quantity)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          sx={{ fontFamily: "monospace", color: "#475569" }}
                        >
                          {display(row?.dealerCode)}
                        </Typography>
                      </TableCell>
                      <TableCell>{display(row?.dealerName)}</TableCell>
                      <TableCell>
                        <Typography
                          onClick={() => handlePhoneClick(row)}
                          sx={{
                            cursor: "pointer",
                            color: "#2563eb",
                            fontWeight: 600,
                            "&:hover": { textDecoration: "underline" },
                          }}
                        >
                          {row?.architechId?.phone
                            ? `+91 ${row.architechId.phone}`
                            : "—"}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          sx={{ color: "#64748b", maxWidth: 160, whiteSpace: "normal" }}
                        >
                          {display(row?.customerDetails)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ color: "#64748b" }}>
                          {row?.updatedAt
                            ? dayjs(row.updatedAt).format("DD MMM YYYY")
                            : "—"}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ color: "#64748b", maxWidth: 120 }}>
                          {display(row?.remarks)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography sx={{ fontWeight: 700, color: "#0d9488" }}>
                          {display(row?.reward)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {row?.status === "PENDING" ? (
                          <Stack direction="row" spacing={1}>
                            <Button
                              variant="contained"
                              size="small"
                              onClick={() =>
                                handleStatusUpdate(row._id, "APPROVED")
                              }
                              sx={{
                                borderRadius: "10px",
                                textTransform: "none",
                                fontWeight: 600,
                                bgcolor: "#16a34a",
                                boxShadow: "none",
                                "&:hover": { bgcolor: "#15803d" },
                              }}
                            >
                              Approve
                            </Button>
                            <Button
                              variant="contained"
                              size="small"
                              onClick={() =>
                                handleStatusUpdate(row._id, "DECLINED")
                              }
                              sx={{
                                borderRadius: "10px",
                                textTransform: "none",
                                fontWeight: 600,
                                bgcolor: "#dc2626",
                                boxShadow: "none",
                                "&:hover": { bgcolor: "#b91c1c" },
                              }}
                            >
                              Decline
                            </Button>
                          </Stack>
                        ) : (
                          <StatusChip status={row?.status} />
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <PanelCard>
              <Stack alignItems="center" sx={{ py: 8, px: 3 }}>
                <PointOfSaleOutlinedIcon
                  sx={{ fontSize: 56, color: "#cbd5e1", mb: 2 }}
                />
                <Typography variant="h6" sx={{ fontWeight: 700, color: "#0f172a" }}>
                  No sales found
                </Typography>
                <Typography sx={{ color: "#64748b", mt: 1, textAlign: "center" }}>
                  Try adjusting your search or date filters.
                </Typography>
              </Stack>
            </PanelCard>
          )}
        </motion.div>

        {!loading && allSales.length > 0 && (
          <motion.div custom={6} initial="hidden" animate="visible" variants={fadeUp}>
            <PanelCard sx={{ mt: 3, p: 2 }}>
              <Stack
                direction={{ xs: "column", sm: "row" }}
                alignItems="center"
                justifyContent="space-between"
                spacing={2}
              >
                <Typography sx={{ color: "#64748b", fontWeight: 500 }}>
                  Showing {(currentPage - 1) * countPerPage + 1}–
                  {Math.min(currentPage * countPerPage, allSales.length)} of{" "}
                  {allSales.length}
                </Typography>
                <Stack direction="row" alignItems="center" spacing={2}>
                  <Typography variant="body2" sx={{ color: "#64748b" }}>
                    Rows per page
                  </Typography>
                  <Box
                    component="select"
                    value={countPerPage}
                    onChange={(e) => {
                      setCountPerPage(Number(e.target.value));
                      setCurrentPage(1);
                    }}
                    sx={{
                      border: `1px solid ${alpha("#11409B", 0.2)}`,
                      borderRadius: "10px",
                      px: 1.5,
                      py: 0.75,
                      fontSize: "0.875rem",
                      color: "#0f172a",
                      bgcolor: "#fff",
                      outline: "none",
                    }}
                  >
                    <option value={10}>10</option>
                    <option value={25}>25</option>
                    <option value={30}>30</option>
                    <option value={50}>50</option>
                  </Box>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <IconButton
                      onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
                      disabled={currentPage === 1}
                      sx={{
                        border: "1px solid",
                        borderColor: alpha("#11409B", 0.15),
                        borderRadius: "12px",
                        bgcolor: "#fff",
                        "&:hover": { bgcolor: alpha("#11409B", 0.06) },
                      }}
                    >
                      <KeyboardArrowLeftIcon />
                    </IconButton>
                    <Typography sx={{ fontWeight: 600, minWidth: 48, textAlign: "center" }}>
                      {currentPage} / {totalPages}
                    </Typography>
                    <IconButton
                      onClick={() =>
                        setCurrentPage((p) => Math.min(p + 1, totalPages))
                      }
                      disabled={currentPage >= totalPages}
                      sx={{
                        border: "1px solid",
                        borderColor: alpha("#11409B", 0.15),
                        borderRadius: "12px",
                        bgcolor: "#fff",
                        "&:hover": { bgcolor: alpha("#11409B", 0.06) },
                      }}
                    >
                      <KeyboardArrowRightIcon />
                    </IconButton>
                  </Stack>
                </Stack>
              </Stack>
            </PanelCard>
          </motion.div>
        )}
      </Box>

      <Modal open={modalOpen} onClose={handleCloseModal}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: { md: 420, xs: "90%" },
            maxHeight: "90vh",
            overflow: "auto",
            bgcolor: "#fff",
            borderRadius: "24px",
            boxShadow: "0 24px 80px rgba(15, 23, 42, 0.2)",
            outline: "none",
          }}
        >
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{
              px: 3,
              py: 2,
              borderBottom: `1px solid ${alpha("#11409B", 0.08)}`,
            }}
          >
            <Typography sx={{ fontWeight: 700, color: "#0f172a" }}>
              Architect details
            </Typography>
            <IconButton onClick={handleCloseModal} size="small">
              <CloseIcon />
            </IconButton>
          </Stack>

          {selectedSales && (
            <Box sx={{ p: 3 }}>
              <Stack alignItems="center" spacing={2} mb={3}>
                <Avatar
                  src={selectedSales.architechId?.pictureUrl}
                  alt={selectedSales.architechId?.username || "Architect"}
                  sx={{
                    width: 88,
                    height: 88,
                    border: "3px solid #fff",
                    boxShadow: "0 8px 24px rgba(17,64,155,0.2)",
                    background: "linear-gradient(135deg, #11409B, #2563eb)",
                    fontWeight: 700,
                    fontSize: "1.5rem",
                  }}
                >
                  {(selectedSales.architechId?.username || "?").charAt(0).toUpperCase()}
                </Avatar>
                <Typography sx={{ fontWeight: 700, color: "#0f172a" }}>
                  {selectedSales.architechId?.username || "—"}
                </Typography>
              </Stack>

              <Stack spacing={1.5}>
                {[
                  ["Phone", selectedSales.architechId?.phone],
                  ["Email", selectedSales.architechId?.email],
                  [
                    "Reward points",
                    selectedSales.architechId?.rewardPoint,
                  ],
                ].map(([label, value]) => (
                  <Box
                    key={label}
                    sx={{
                      p: 1.5,
                      borderRadius: "12px",
                      bgcolor: alpha("#11409B", 0.04),
                      border: `1px solid ${alpha("#11409B", 0.06)}`,
                    }}
                  >
                    <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 600 }}>
                      {label}
                    </Typography>
                    <Typography sx={{ fontWeight: 600, color: "#0f172a" }}>
                      {display(value)}
                    </Typography>
                  </Box>
                ))}
              </Stack>
            </Box>
          )}
        </Box>
      </Modal>
    </HomeLayout>
  );
};
